import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BaseRes } from 'types/base';
import request from 'apis';
import NormalContract from './normal-contract';
import { LANGUAGE_MAP, LanguageType } from 'constant/language';
import contractPrintUtils, {
  ContractLang,
  ContractPrintProduct,
  ContractStyleType,
  ContractType,
} from './utils';
import { useToggle } from 'react-use';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { useAppSelector } from 'store/hooks';
import { getMemberByIdV2 } from 'apis/ums';
import { sendSignedContractEmail } from 'apis/oms';
import { OMS_RECYCLE_ORDER_TYPE_MAP } from 'commons/options';
import { SHOP_MAP } from 'constant/shop';
import getProductDisplayInfo, {
  buildProductNameWithAttributeRemarks,
} from 'utils/get-product-display-info';
import ReturnContract from './return-contract';
import { Button, Modal, message } from 'antd';
import { generatePdfBlobFromContainer } from 'utils/generateDempyouPdf';
import i18n from 'i18n';
import LOCALS from 'commons/locals';

const OmsRecycleOrderContract = () => {
  const [isLoading, setIsLoading] = useToggle(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasPrintedRef = useRef(false);
  const [sending, setSending] = useState(false);
  const [signatureImage, setSignatureImage] = useState<string>();
  const [orderIdList] = useQueryState(
    'orderIdList',
    parseAsArrayOf(parseAsString).withDefault([]),
  );

  const [contractType] = useQueryState(
    'contractType',
    parseAsString.withDefault('normal'),
  );
  const [sendContract] = useQueryState(
    'sendContract',
    parseAsString.withDefault(''),
  );
  const [signatureKey] = useQueryState(
    'signatureKey',
    parseAsString.withDefault(''),
  );
  const isSendContractMode = sendContract === '1';
  const [parsedData, setParsedData] = useState<{
    contractStyleType: ContractStyleType;
    storeId: number;
    submissionId: number;
    contractLang: ContractLang;
    memberId: number;
    headList: { label: string; value: string }[];
    userInfoRenderLineList: string[];
    contractList: {
      type: ContractType;
      productList: ContractPrintProduct[];
    }[];
  }>();

  const {
    productCategoryCascaderOptions,
    materialCascaderOptionsMap,
    colorSelectOptions,
    rankSelectOptions,
    stampSelectOptions,
    hardwareSelectOptions,
    accessorySelectOptions,
    staffSelectOptions,
  } = useAppSelector(selectGlobalInfo);

  useEffect(() => {
    if (!signatureKey) return;

    const storedSignature = sessionStorage.getItem(signatureKey);
    if (storedSignature) {
      setSignatureImage(storedSignature);
    }
  }, [signatureKey]);

  useEffect(() => {
    const uniqueOrderIdList = Array.from(new Set(orderIdList));

    setParsedData(undefined);
    hasPrintedRef.current = false;

    if (!rankSelectOptions.length) return;
    if (!colorSelectOptions.length) return;
    if (!stampSelectOptions.length) return;
    if (!hardwareSelectOptions.length) return;
    if (!accessorySelectOptions.length) return;
    if (!productCategoryCascaderOptions.length) return;
    if (!staffSelectOptions.length) return;
    if (Object.keys(materialCascaderOptionsMap).length === 0) return;

    if (uniqueOrderIdList.length === 0) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    Promise.all(
      uniqueOrderIdList.map((orderId) => {
        return request.post<
          BaseRes<{
            omsRecycleOrder: {
              id: string;
              code: string;
              submissionId: number | null;
              memberId: number;
              storeId: number;
              status: number;
              isAuthentic: 0 | 1; // 1: 真品, 0: 非真品
              createTime: string;
              staffName: string | null;
              createBy: number;
              isTradeIn: 0 | 1; // 是否是置换， 0-否 1-是
              settlementType: 1 | 2 | null; // 结算方式， 1-现金，2-银行转账
              type: 0 | 1 | 2; // 0-未确认，1-寄卖，2-回收
              payoutDetail: string | null;
            };
            omsRecycleOrderItem: {
              agreeFinalValuationTime: string | null;
              finalSalePrice: number | null;
              finalRecyclePrice: number | null;
              guestRemarks: string | null;
            };
            omsRecycleOrderProduct: {
              id: number;
              brandName: string | null;
              name: string | null;
              attrRankDesc: string | null;
              productCategoryId: number | null;
              attrStamp: string | null;
              rank: string | null;
              attrColor: string | null;
              attrMaterial: string | null;
              attrHardware: string | null;
              attrAccessory: string | null;
              attrMaterialRemark: string | null;
              attrColorRemark: string | null;
              attrStampRemark: string | null;
              attrHardwareRemark: string | null;
              fullStamp: string | null;
            };
            omsRecycleOrderLogistics: {
              phone: string | null;
              phone2: string | null;
              name: string | null;
              country: string | null;
              city: string | null;
              detailAddress: string | null;
              postCode: string | null;
            };
            i18nList:
              | {
                  lang: Extract<LanguageType, 'ja' | 'zh_CN' | 'zh_TW'>;
                  name: string | null;
                  attrRankDesc: string | null;
                  attrSize: string | null;
                  detailTitle: string | null;
                }[]
              | null;
          }>
        >(`/oms/recycleOrder/info/${orderId}`);
      }),
    )
      .then(async (res) => {
        const authenticList = res.filter(
          (item) => !!item.data.omsRecycleOrder.isAuthentic,
        );
        if (authenticList.length === 0) {
          return;
        }

        const validate = contractPrintUtils.validateOrderList(
          authenticList.map((item) => ({
            type: item.data.omsRecycleOrder.type,
            submissionId: item.data.omsRecycleOrder.submissionId,
          })),
        );
        if (!validate) return;

        const firstOrderData = authenticList[0].data;
        const {
          submissionId,
          storeId,
          createTime,
          staffName,
          createBy,
          isTradeIn,
          settlementType,
          type,
          memberId,
          payoutDetail,
        } = firstOrderData.omsRecycleOrder;
        const { phone, phone2, name, country, city, detailAddress, postCode } =
          firstOrderData.omsRecycleOrderLogistics;
        const { agreeFinalValuationTime } = firstOrderData.omsRecycleOrderItem;
        if (submissionId === null) return;

        const contractLang = contractPrintUtils.getContractLang(storeId);
        const contractDataLang =
          contractPrintUtils.getContractDataLang(storeId);
        const headList = [
          {
            label: {
              [LANGUAGE_MAP.EN]: 'Receipt No.',
              [LANGUAGE_MAP.JA]: '伝票番号',
              [LANGUAGE_MAP.ZH_CN]: '单据编号',
              [LANGUAGE_MAP.FR]: 'Numéro de Reçu',
            }[contractLang],
            value: submissionId.toString(),
            hidden: false,
          },
          {
            label: {
              [LANGUAGE_MAP.EN]: 'Date',
              [LANGUAGE_MAP.JA]: '受付日',
              [LANGUAGE_MAP.ZH_CN]: '日期',
              [LANGUAGE_MAP.FR]: 'Date',
            }[contractLang],
            value: contractPrintUtils.getDate({
              agreeFinalValuationTime: agreeFinalValuationTime ?? undefined,
              createTime,
            }),
            hidden: false,
          },
          {
            label: {
              [LANGUAGE_MAP.EN]: 'Accountable',
              [LANGUAGE_MAP.JA]: '担当者',
              [LANGUAGE_MAP.ZH_CN]: '担当者',
              [LANGUAGE_MAP.FR]: 'Responsable',
            }[contractLang],
            value: contractPrintUtils.getStaffName({
              staffName: staffName ?? undefined,
              createBy: createBy,
              staffSelectOptions: staffSelectOptions.map((option) => ({
                label: option.label,
                value: option.value as number,
              })),
            }),
            hidden: false,
          },
          {
            label: {
              [LANGUAGE_MAP.EN]: 'Transaction',
              [LANGUAGE_MAP.JA]: 'ご清算区分',
              [LANGUAGE_MAP.ZH_CN]: '结算方式',
              [LANGUAGE_MAP.FR]: 'Transaction',
            }[contractLang],
            value: contractPrintUtils.getSettlementType({
              isTradeIn: isTradeIn,
              contractLang: contractLang,
              settlementType,
            }),
            hidden: contractType === 'return',
          },
        ].filter((d) => !d.hidden);

        let idCertificate = '';
        const memberRes = await getMemberByIdV2(memberId);
        idCertificate = memberRes.idCertificate ?? '';

        const userInfoRenderLineList =
          await contractPrintUtils.getUserInfoRenderLineList({
            phone: phone ?? '',
            phone2: phone2 ?? '',
            storeId,
            idCertificate: idCertificate ?? '',
            name: name ?? '',
            country: country ?? '',
            city: city ?? '',
            detailAddress: detailAddress ?? '',
            postCode: postCode ?? '',
            payoutDetail: payoutDetail ?? '',
          });

        const productList: ContractPrintProduct[] = authenticList.map(
          (data) => {
            const { finalSalePrice, finalRecyclePrice, guestRemarks } =
              data.data.omsRecycleOrderItem;
            const { i18nList } = data.data;
            const {
              brandName,
              id,
              name,
              attrRankDesc,
              productCategoryId,
              attrStamp,
              rank,
              attrColor,
              attrMaterial,
              attrHardware,
              attrAccessory,
              attrMaterialRemark,
              attrColorRemark,
              attrStampRemark,
              attrHardwareRemark,
              fullStamp,
            } = data.data.omsRecycleOrderProduct;

            const productDisplayInfo = getProductDisplayInfo({
              productInfo: {
                name: name ?? '',
                rankDescription: attrRankDesc ?? '',
                productCategoryId: productCategoryId ?? 0,
                stamp: attrStamp ?? '',
                rank: rank ?? '',
                color: attrColor ?? '',
                material: attrMaterial ?? '',
                hardware: attrHardware ?? '',
                accessory: attrAccessory ?? '',
                i18nList:
                  i18nList?.map(({ lang, name, attrRankDesc }) => {
                    return {
                      lang,
                      name: name ?? '',
                      attrRankDesc: attrRankDesc ?? '',
                    };
                  }) ?? [],
              },
              lang: contractDataLang,
              options: {
                productCategoryCascaderOptions,
                materialCascaderOptionsMap,
                colorSelectOptions,
                rankSelectOptions,
                stampSelectOptions,
                hardwareSelectOptions,
                accessorySelectOptions,
              },
            });

            const productName = buildProductNameWithAttributeRemarks({
              name: productDisplayInfo.name,
              material: productDisplayInfo.material,
              materialRemark: attrMaterialRemark ?? '',
              color: productDisplayInfo.color,
              colorRemark: attrColorRemark ?? '',
              stamp: fullStamp || productDisplayInfo.stamp,
              stampRemark: attrStampRemark ?? '',
              hardware: productDisplayInfo.hardware,
              hardwareRemark: attrHardwareRemark ?? '',
              accessory: productDisplayInfo.accessory,
            });

            let rankDesc = productDisplayInfo.rankDesc || '';
            rankSelectOptions.forEach((item) => {
              rankDesc = rankDesc.replace(item.label || '', '');
              rankDesc = rankDesc.replace(item.labelEn || '', '');
              rankDesc = rankDesc.replace(item.labelJa || '', '');
              rankDesc = rankDesc.replace(item.labelTw || '', '');
              rankDesc = rankDesc.replace(item.labelCn || '', '');
            });

            return {
              price:
                type === OMS_RECYCLE_ORDER_TYPE_MAP.CONSIGNMENT
                  ? (finalSalePrice ?? 0)
                  : (finalRecyclePrice ?? 0),
              brandName: brandName ?? '',
              id,
              name: productName,
              guestRemark: [rankDesc, guestRemarks]
                .filter(Boolean)
                .join(' ')
                .trim(),
            };
          },
        );

        const contractList = contractPrintUtils.getContractPrintList({
          storeId,
          type,
          productList,
          customerOnly: isSendContractMode,
          storeOnly: contractType === 'return' && storeId === SHOP_MAP.HONGKONG,
        });

        setParsedData({
          contractStyleType: storeId === SHOP_MAP.HONGKONG ? 'white' : 'black',
          memberId,
          storeId,
          submissionId,
          contractLang,
          headList,
          userInfoRenderLineList,
          contractList,
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    accessorySelectOptions,
    colorSelectOptions,
    contractType,
    hardwareSelectOptions,
    isSendContractMode,
    materialCascaderOptionsMap,
    orderIdList,
    productCategoryCascaderOptions,
    rankSelectOptions,
    setIsLoading,
    staffSelectOptions,
    stampSelectOptions,
  ]);

  const handleSendContract = useCallback(async () => {
    if (!parsedData) return;

    const orderIdsToSend = Array.from(new Set(orderIdList));
    const pdfFileName = `signed-contract-${parsedData.submissionId}.pdf`;
    if (!orderIdsToSend.length) {
      message.error(String(i18n.t(LOCALS.signed_contract_missing_order_ids)));
      return;
    }

    Modal.confirm({
      title: i18n.t(LOCALS.signed_contract_confirm_send_title),
      content: i18n.t(LOCALS.signed_contract_confirm_send_content),
      okText: i18n.t(LOCALS.signed_contract_confirm_send),
      cancelText: i18n.t(LOCALS.cancel),
      onOk: async () => {
        setSending(true);
        try {
          const { blob, filename: pdfFilename } =
            await generatePdfBlobFromContainer(
              containerRef.current,
              pdfFileName,
            );
          const formData = new FormData();
          formData.append('file', blob, pdfFilename);
          formData.append('omsRecycleOrderIds', orderIdsToSend.join(','));
          await sendSignedContractEmail(formData);
          message.success(
            String(i18n.t(LOCALS.signed_contract_email_sent_success)),
          );
          window.close();
        } catch (error) {
          console.error('send signed contract failed', error);
          message.error(String(i18n.t(LOCALS.signed_contract_send_failed)));
          throw error;
        } finally {
          setSending(false);
        }
      },
    });
  }, [orderIdList, parsedData]);

  useEffect(() => {
    const printableContractTypes = ['normal', 'settlement', 'return'];
    if (
      isLoading ||
      !parsedData ||
      isSendContractMode ||
      !printableContractTypes.includes(contractType) ||
      hasPrintedRef.current
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      hasPrintedRef.current = true;
      window.print();
      window.close();
    }, 666);

    return () => window.clearTimeout(timer);
  }, [contractType, isLoading, isSendContractMode, parsedData]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (parsedData === undefined) {
    return <div>No data</div>;
  }

  return (
    <>
      <div className={isSendContractMode ? 'pb-28 print:pb-0' : undefined}>
        <div ref={containerRef}>
          {contractType === 'normal' || contractType === 'settlement'
            ? parsedData.contractList.map((contract, index) => (
                <NormalContract
                  key={index}
                  storeId={parsedData.storeId}
                  headList={parsedData.headList}
                  contractLang={parsedData.contractLang}
                  contractType={contract.type}
                  userInfoRenderLineList={parsedData.userInfoRenderLineList}
                  memberId={parsedData.memberId}
                  contractPrintProductList={contract.productList}
                  contractStyleType={parsedData.contractStyleType}
                  isSettlement={contractType === 'settlement'}
                  signatureImage={signatureImage || undefined}
                />
              ))
            : null}

          {contractType === 'return'
            ? parsedData.contractList.map((contract, index) => (
                <ReturnContract
                  key={index}
                  storeId={parsedData.storeId}
                  headList={parsedData.headList}
                  contractLang={parsedData.contractLang}
                  contractType={contract.type}
                  userInfoRenderLineList={parsedData.userInfoRenderLineList}
                  memberId={parsedData.memberId}
                  contractPrintProductList={contract.productList}
                  contractStyleType={parsedData.contractStyleType}
                />
              ))
            : null}
        </div>
      </div>

      {isSendContractMode && parsedData.contractList.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-center gap-4 border-t bg-white px-6 py-4 shadow-lg print:hidden">
          <span className="text-sm text-gray-600">
            {i18n.t(LOCALS.signed_contract_review_before_send)}
          </span>
          <Button onClick={() => window.close()} disabled={sending}>
            {i18n.t(LOCALS.signed_contract_close)}
          </Button>
          <Button type="primary" loading={sending} onClick={handleSendContract}>
            {i18n.t(LOCALS.signed_contract_confirm_send)}
          </Button>
        </div>
      )}
    </>
  );
};

export default OmsRecycleOrderContract;
